import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  ArrowRight,
  BadgeCheck,
  Bed,
  CalendarDays,
  Clock3,
  Gauge,
  Play,
  ShieldCheck,
  Square,
  UserRoundCheck,
  X,
} from "lucide-react";
import { driverShiftAction, listDriverCompliance } from "../api";
import type { AuthUser, DriverCompliance, Vehicle } from "../types";

type DriverFilter = "attention" | "driving" | "rest" | "all";

const minutesLabel = (value: number) => {
  const absolute = Math.max(0, value);
  return `${Math.floor(absolute / 60)}h ${absolute % 60}m`;
};

export function DriverPanel({
  open,
  onClose,
  vehicles,
  user,
  onSelectVehicle,
}: {
  open: boolean;
  onClose: () => void;
  vehicles: Vehicle[];
  user: AuthUser;
  onSelectVehicle: (vehicle: Vehicle) => void;
}) {
  const [rows, setRows] = useState<DriverCompliance[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [filter, setFilter] = useState<DriverFilter>("attention");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    const refresh = () => {
      listDriverCompliance()
        .then((items) => {
          setRows(items);
          setSelectedId((current) =>
            items.some((item) => item.driver_id === current)
              ? current
              : items[0]?.driver_id ?? null,
          );
          setError("");
        })
        .catch((cause) =>
          setError(cause instanceof Error ? cause.message : "No fue posible cargar conductores"),
        );
    };
    refresh();
    window.addEventListener("gsf:driver-updated", refresh);
    return () => window.removeEventListener("gsf:driver-updated", refresh);
  }, [open]);

  const visibleRows = useMemo(
    () =>
      rows.filter((row) => {
        if (filter === "attention") return row.compliance_status !== "clear";
        if (filter === "driving") return row.shift_status === "driving";
        if (filter === "rest") return ["rest", "off_duty"].includes(row.shift_status);
        return true;
      }),
    [filter, rows],
  );
  const selected =
    visibleRows.find((row) => row.driver_id === selectedId) || visibleRows[0] || rows[0];
  const selectedVehicle = vehicles.find((vehicle) => vehicle.id === selected?.vehicle_id);
  const canControl = ["superadmin", "fleet_admin", "operator", "dispatcher"].includes(user.role);
  const summary = useMemo(
    () => ({
      blocked: rows.filter((row) => row.compliance_status === "blocked").length,
      warning: rows.filter((row) => row.compliance_status === "warning").length,
      driving: rows.filter((row) => row.shift_status === "driving").length,
    }),
    [rows],
  );

  async function action(kind: "start_rest" | "resume" | "end_shift") {
    if (!selected) return;
    setPending(true);
    setError("");
    try {
      const updated = await driverShiftAction(selected.driver_id, kind);
      setRows((current) =>
        current.map((row) => row.driver_id === updated.driver_id ? updated : row),
      );
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "No fue posible actualizar el turno");
    } finally {
      setPending(false);
    }
  }

  if (!open) return null;
  return (
    <div className="admin-overlay" role="presentation" onMouseDown={onClose}>
      <aside className="admin-panel driver-panel" role="dialog" aria-modal="true" aria-labelledby="driver-title" onMouseDown={(event) => event.stopPropagation()}>
        <header>
          <div>
            <span className="eyebrow">CONTROL DE OPERADORES · POLÍTICA INTERNA CONFIGURABLE</span>
            <h2 id="driver-title">Conductores y turnos</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Cerrar conductores"><X size={18} /></button>
        </header>
        <section className="admin-panel__summary">
          <span><AlertTriangle size={17} /><b>{summary.blocked}</b> bloqueados</span>
          <span><Clock3 size={17} /><b>{summary.warning}</b> por atender</span>
          <span><Gauge size={17} /><b>{summary.driving}</b> conduciendo</span>
        </section>
        <div className="driver-panel__body">
          <section className="driver-queue">
            <div className="driver-filters">
              {(["attention", "driving", "rest", "all"] as DriverFilter[]).map((item) => (
                <button type="button" className={filter === item ? "is-active" : ""} onClick={() => setFilter(item)} key={item}>
                  {{ attention: "ATENCIÓN", driving: "CONDUCIENDO", rest: "DESCANSO", all: "TODOS" }[item]}
                </button>
              ))}
            </div>
            <div className="driver-list">
              {visibleRows.map((driver) => (
                <button
                  type="button"
                  className={`driver-item driver-item--${driver.compliance_status}${selected?.driver_id === driver.driver_id ? " is-selected" : ""}`}
                  onClick={() => setSelectedId(driver.driver_id)}
                  key={driver.driver_id}
                >
                  <span><UserRoundCheck size={16} /></span>
                  <span>
                    <small>{driver.employee_code} · {driver.vehicle_unit_code || "SIN UNIDAD"}</small>
                    <strong>{driver.full_name}</strong>
                    <em>{driver.shift_status.replaceAll("_", " ")} · {minutesLabel(driver.driving_minutes)} conducción</em>
                  </span>
                  <b>{driver.compliance_status === "blocked" ? "BLOQUEADO" : driver.compliance_status === "warning" ? "ATENCIÓN" : "LIBERADO"}</b>
                </button>
              ))}
              {!visibleRows.length && <p className="maintenance-empty">SIN OPERADORES EN ESTA VISTA</p>}
            </div>
          </section>
          <section className="driver-detail">
            {selected && (
              <>
                <div className={`driver-status driver-status--${selected.compliance_status}`}>
                  {selected.shift_status === "driving" ? <Gauge size={26} /> : <Bed size={26} />}
                  <span><small>ESTADO OPERATIVO</small><b>{selected.compliance_status.toUpperCase()}</b></span>
                </div>
                <div className="driver-detail__title">
                  <span className="eyebrow">{selected.employee_code} · {selected.vehicle_unit_code || "SIN UNIDAD"}</span>
                  <h3>{selected.full_name}</h3>
                  <p>{selected.license_class} · {selected.license_number}</p>
                </div>
                <div className="driver-documents">
                  <article><BadgeCheck size={17} /><span><small>VIGENCIA LICENCIA</small><strong>{new Date(`${selected.license_expires_on}T12:00:00`).toLocaleDateString("es-MX")}</strong></span></article>
                  <article><CalendarDays size={17} /><span><small>APTITUD MÉDICA</small><strong>{new Date(`${selected.medical_expires_on}T12:00:00`).toLocaleDateString("es-MX")}</strong></span></article>
                </div>
                <div className="driver-hours">
                  {[
                    ["CONDUCCIÓN DIARIA", selected.driving_minutes, selected.policy_json.daily_driving_minutes],
                    ["JORNADA", selected.duty_minutes, selected.policy_json.duty_minutes],
                    ["CONDUCCIÓN CONTINUA", selected.continuous_driving_minutes, selected.policy_json.continuous_driving_minutes],
                  ].map(([label, used, limit]) => (
                    <article key={label}>
                      <span><small>{label}</small><strong>{minutesLabel(Number(used))} / {minutesLabel(Number(limit))}</strong></span>
                      <i><em style={{ width: `${Math.min(100, Number(used) / Number(limit) * 100)}%` }} /></i>
                    </article>
                  ))}
                </div>
                <div className="driver-reasons">
                  <span className="eyebrow">CONDICIONES DETECTADAS</span>
                  {selected.reasons.map((reason) => <article key={reason}><AlertTriangle size={13} /><strong>{reason}</strong></article>)}
                  {!selected.reasons.length && <article className="is-clear"><ShieldCheck size={13} /><strong>Sin restricciones activas</strong></article>}
                </div>
                <div className="driver-action-copy"><ShieldCheck size={18} /><span><small>ACCIÓN REQUERIDA</small><strong>{selected.required_action}</strong></span></div>
                {canControl && selected.shift_id && (
                  <div className="driver-actions">
                    {selected.shift_status !== "rest" && <button type="button" disabled={pending} onClick={() => action("start_rest")}><Bed size={13} /> REGISTRAR DESCANSO</button>}
                    {selected.shift_status === "rest" && <button type="button" disabled={pending || selected.compliance_status === "blocked"} onClick={() => action("resume")}><Play size={13} /> REANUDAR TURNO</button>}
                    <button type="button" disabled={pending} onClick={() => action("end_shift")}><Square size={13} /> CERRAR TURNO</button>
                  </div>
                )}
                {selectedVehicle && (
                  <button type="button" className="primary-button" onClick={() => { onSelectVehicle(selectedVehicle); onClose(); }}>
                    ABRIR UNIDAD <ArrowRight size={14} />
                  </button>
                )}
              </>
            )}
          </section>
        </div>
        {error && <div className="admin-message admin-message--error">{error}</div>}
      </aside>
    </div>
  );
}
